import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';

/** Custom validator – kept here so every consumer gets the same rule. */
export function emailFormatValidator(
  control: AbstractControl
): ValidationErrors | null {
  const EMAIL_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
  const value = control.value?.trim();
  if (!value) return null; // let `required` handle empty
  return EMAIL_REGEX.test(value) ? null : { invalidEmail: true };
}

@Component({
  selector: 'app-login-email-input',
  standalone: false,
  templateUrl: './email-input.component.html',
  styleUrl: './email-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LoginEmailInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => LoginEmailInputComponent),
      multi: true,
    },
  ],
})
export class LoginEmailInputComponent
  implements ControlValueAccessor, Validator, OnInit, OnDestroy
{
  /** Label shown above the input. Override per page if needed. */
  @Input() label = 'Email';
  /** Placeholder text. */
  @Input() placeholder = 'name@company.com';
  /** id attribute – must be unique when used multiple times on the same page. */
  @Input() inputId = 'email';

  /** Internal FormControl that owns the value + validation. */
  readonly control = new FormControl<string>('', [
    Validators.required,
    emailFormatValidator,
  ]);

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private sub!: Subscription;

  ngOnInit(): void {
    this.sub = this.control.valueChanges.subscribe((val) =>
      this.onChange(val ?? '')
    );
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  writeValue(value: string): void {
    this.control.setValue(value ?? '', { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }

  // ── Validator ─────────────────────────────────────────────────────────────

  validate(_: AbstractControl): ValidationErrors | null {
    return this.control.valid ? null : this.control.errors;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  onBlur(): void {
    this.control.markAsTouched();
    this.onTouched();
  }
}
